import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import useFetch from "hooks/useFetch";
import CustomButton from "components/Button";
import { IconButton, Typography } from "@mui/material";
import CustomInput from "components/Input";
import { getToken } from "utils/getToken";
import CloseButtonIcon from "assets/icons/Close";
import useIsTablet from "hooks/useIsTablet";
import { useState } from "react";
import { toast } from "react-toastify";
import { useAuthStore } from "store/authStore";

interface IModalCheckDomain {
  open: boolean;
  setIsModalOpen: (value: boolean) => void;
}

const TelegramUserNameModal = ({ open, setIsModalOpen }: IModalCheckDomain) => {
  const token = getToken();
  const { t } = useTranslation();
  const { setUser } = useAuthStore();
  const isTablet = useIsTablet();
  const [inputValue, setInputValue] = useState<string>("");
  const [inputError, setInputError] = useState<string>("");
  const telegramNameRegex = /^@[A-Za-z0-9_]{5,32}$/;

  const { loading: loadingTelegramName, triggerFetch: changeTelegramName } = useFetch({
    url: "profile/update-telegram-username",
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: {
      telegramUsername: inputValue,
    },
  });

  const { triggerFetch: getUserInfo } = useFetch({
    url: "profile/user-info",
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const handleUserInfo = async () => {
    const res = await getUserInfo();
    if (res?.data) {
      localStorage.setItem("userData", JSON.stringify(res.data));
      setUser(res?.data);
      return;
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = event.target;
    setInputValue(value);
  };

  const handleSubmit = async () => {
    if (!inputValue) {
      setInputError("thisfieldisrequired");
      return;
    }

    if (!telegramNameRegex.test(inputValue)) {
      setInputError("telegramNameError");
      return;
    }

    const updatedTelegramName = await changeTelegramName();

    if (updatedTelegramName.success) {
      void handleUserInfo();
      toast.success(t("successfulTelegramNameChange"));
      handleModalClose();
      return;
    }

    toast.error(t("unsuccessfulTelegramNameChange"));
  };

  const handleModalClose = () => {
    setInputError("");
    setInputValue("");
    setIsModalOpen(false);
  };

  return (
    <Modal
      open={open}
      onClose={handleModalClose}
      aria-labelledby="modal-check-domain-title"
      aria-describedby="modal-check-domain-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          borderRadius: "8px",
          padding: "32px 24px 24px 24px",
          bgcolor: "#FFFFFF",
          boxShadow: "0 0 4px 0 rgba(165, 163, 174, 0.3);",
          width: "90%",
          maxWidth: "520px",
          height: "auto",
          maxHeight: "90dvh",
          overflowY: "auto",
        }}
      >
        <IconButton
          sx={{ position: "absolute", top: 16, right: 16, width: 32, height: 32 }}
          aria-label="modal close button"
          onClick={handleModalClose}
        >
          <CloseButtonIcon />
        </IconButton>
        <Typography
          id="modal-modal-title"
          variant="h2"
          sx={{
            marginBottom: "14px",
            fontSize: "24px",
            fontWeight: 400,
            color: "#2E263D",
          }}
        >
          {t("userTelegramModaltitle")}
        </Typography>
        <Typography
          sx={{
            marginBottom: "40px",
            fontSize: "14px",
            lineHeight: "1.35",
            fontWeight: 400,
            color: "#2E263D",
          }}
        >
          {t("userTelegramModalDescription")}
        </Typography>
        <Box sx={{ marginBottom: "24px" }}>
          <CustomInput
            required
            fullWidth
            id="telegramName"
            value={inputValue}
            placeholder=""
            name="telegramName"
            height="40px"
            label={t("userTelegramNameLabel")}
            autoFocus={isTablet ? false : true}
            customBgColor="#F6F7FB"
            customFontSize="12px"
            helperText={t(inputError)}
            error={inputError ? true : false}
            onChange={handleInputChange}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            gap: "8px",
            flexDirection: isTablet ? "column-reverse" : "row",
            alignItems: "center",
          }}
        >
          <CustomButton
            width="100%"
            height={40}
            textButton={t("cancel")}
            customColor="#6D6777"
            customBorderColor="#C9D0D8"
            customHoverColor="transparent"
            customBorderRadius="8px"
            customFontSize="14px"
            customTextTransform="none"
            onClick={handleModalClose}
          />
          <CustomButton
            width="100%"
            textButton={t("save")}
            height={40}
            customBgColor="#F4701B"
            customHoverColor="#F4701B"
            customBorderRadius="8px"
            customFontSize="14px"
            customTextTransform="none"
            onClick={handleSubmit}
            loading={loadingTelegramName}
          />
        </Box>
      </Box>
    </Modal>
  );
};

export default TelegramUserNameModal;
